import { Axis, FrenzyAxis, FrenzyLine, FrenzyDirection, FrenzyPolygon, LeptonState, LeptonDirection, FrenzyXYTuple, Direction, Point, FrenzyRotation, Rotation } from './Model';
import { HitBox } from './utils/HitBox';
import { describeLine, describeLines, length } from './utils/Line';
import { areSame } from './utils/Point';
import { boundingRectangleForPolygon, isInsideComplexPolygons } from './utils/Polygon';

export const minMax = (value1: number, value2:number):[number, number] => { if (value1 < value2) return [value1, value2]; else return [value2, value1]; };

export function clampAboveZero(n: number) {
  if (n < 0) return 0;
  return n;
}

export const isInRange = (firstExtent: number, secondExtent: number, x: number):boolean => {
  const [lowestX, highestX] = minMax(firstExtent, secondExtent);
  return (lowestX <= x) && (x <= highestX);
}





export const describePoint= (p: Point) => {
  return `(${p[0]},${p[1]})`;
};


export const findIntersectingLines = (x: number, y: number, lines: FrenzyLine[], optionalLineAxis?: FrenzyAxis):FrenzyLine[] => {
  const axisFiltered = optionalLineAxis
    ? lines.filter(l => l.direction.axis === optionalLineAxis) : lines;

  const filtered = axisFiltered.filter(line => {
    return ((line.start[0] === x || line.start[1] === y) 
      || (line.end[0] === x || line.end[1] === y));
  });

  // console.log(`${lines.length} filtered to`, filtered);

  const hits = filtered.filter(line => {
    const axis = line.direction.axis;
    return axis.intersects(x, y, line);
  });

  return hits || [];
};

// export const findIntersectingLine = (x: number, y: number, lines: FrenzyLine[], optionalLineAxis?: FrenzyAxis) => {
//   const intersects = findIntersectingLines(x, y, lines, optionalLineAxis);

//   return intersects && intersects.length > 0 && intersects[0];
// };

// Where would the lepton go next if unobstructed?
export const nextLeptonPoint = (lepton: LeptonState):FrenzyXYTuple => {
  const { direction, x, y } = lepton;
  return direction.positionModifier({x: lepton.x, y: lepton.y}, 1);
}



// export const findFillRectanglesForPolygon = (boundingPolygon: FrenzyPolygon, maxX: number, maxY: number) => {
//   const { polygon } = boundingPolygon;

//   const {top, right, bottom, left} = boundingRectangleForPolygon(polygon.polygonLines);

//   // We now have the top-left corner of the polygon.
//   // Proceed horizontally until we hit the largest X on this Y.
//   // That is our width for this poly.
//   // Now work downwards, checking the entire row (minX, minY) -> (maxX, minY).
//   // If there are any intersections, we must STOP and consider that polygon DONE.

//   let x = left + 1;
//   console.log(`checking ${x} vs ${right}`);
//   do {
//     console.log(`Checking Intersect of ${x}, ${top} with ${describePolygon(polygon)}`);
//     const intersect = findIntersectingLines(x, top, polygon, Axis.VERTICAL);
//     console.log(`Intersect of ${x}, ${top} is ${intersect}`);
//     x = x + 1;
//   } while (x <= right);

//   return [{
//     top,
//     left,
//     width: right - left,
//     height: bottom - top
//   }];
// };

const isOutsideBoundingBox = (x: number, y: number, filledPolygon: FrenzyPolygon) => {
  const {top, right, bottom, left} = boundingRectangleForPolygon(filledPolygon.polygonLines);

  return ((x < left) || (x > right)) || ((y < top) || (y > bottom));
};

const isInsideBoundingBox = (x: number, y: number, filledPolygon: FrenzyPolygon) => {
  return !isOutsideBoundingBox(x, y, filledPolygon);
};

const isOutsideAllBoundingBoxes = (x: number, y: number, filledPolygons: FrenzyPolygon[]) => {
  if (filledPolygons.length === 0) return true;
  const boxesWeAreOutside = filledPolygons.filter(fp => isOutsideBoundingBox(x, y, fp));
  return boxesWeAreOutside.length === filledPolygons.length;
};

const isInsideSimpleRectangularPolygon = (x: number, y: number, filledPolygons: FrenzyPolygon[]) => {
  // If a polygon has exactly 4 lines then we can consider its bounding box
  // and easily detect if a point is inside.
  const rects = filledPolygons.filter(fp => fp.polygonLines.length === 4);
  const isInsideOne = rects.find(r => isInsideBoundingBox(x, y, r));
  return typeof isInsideOne !== 'undefined';
};

export const wouldBeInsideExistingFilledArea = (currentX: number, currentY: number, direction: FrenzyDirection, filledPolygons: FrenzyPolygon[]) => {
  if (filledPolygons && filledPolygons.length > 0) {
    const {x, y} = direction.traverse(currentX, currentY);

    // If it's outside all of the bounding boxes it's definitely NOT
    // inside
    if (isOutsideAllBoundingBoxes(x, y, filledPolygons)) {
      return false;
    }

   //  console.log(`Position ${x},${y} is not outside bounding boxes`);

    if (isInsideSimpleRectangularPolygon(x, y, filledPolygons)) {
      return true;
    }
		// console.log(`Hit non-simple case`);
		return isInsideComplexPolygons(x, y, filledPolygons);

  }
	return false;
};

export const addNonEmptyLines = (acc: FrenzyLine[], linesArray?: FrenzyLine[]): FrenzyLine[] => {
		if (linesArray && linesArray.length > 0 && linesArray[0] !== null) {
			return [...acc, ...linesArray]
		} else {
			return acc;
		}
  }

  const defaultComparator = (a:any, b:any): Boolean => {
    return JSON.stringify(a) === JSON.stringify(b)
  }
  
export function dedupe<T>(arr: T[], comparator: (a:T, b:T) =>Boolean = defaultComparator):T[] {
  return arr.filter((el, i) => {
    return i === arr.findIndex(o => comparator(o, el));
  });
}

export function determineLineDirection(start: Point, end: Point): FrenzyDirection {
  if (start[0] === end[0]) {
    return start[1] < end[1] ? Direction.DOWN : Direction.UP
  } else {
    return start[0] < end[0] ? Direction.RIGHT : Direction.LEFT
  }
}

export function oppositeOf(d: FrenzyDirection) {
  switch (d.oppositeLabel) {
    case 'LEFT':
      return Direction.RIGHT;
    case 'RIGHT':
      return Direction.LEFT;
    case 'UP':
      return Direction.DOWN;
    case 'DOWN':
      return Direction.UP;
  }
}

export function oppositeRotation(r: FrenzyRotation) {
  switch (r.label) {
    case 'CLOCKWISE':
      return Rotation.CCW;
    case 'COUNTER-CLOCKWISE':
      return Rotation.CW;
  }
}

export function pointsToLine(start: Point, end: Point): FrenzyLine {
  return {
    direction: determineLineDirection(start, end),
    start,
    end
  }
}

export function areSamePoint(xyPoint: FrenzyXYTuple, pointB: Point):boolean {
  return xyPoint.x === pointB[0] && xyPoint.y == pointB[1];
}
export function areSameTuple(xyPointA: FrenzyXYTuple, xyPointB: FrenzyXYTuple):boolean {
  return xyPointA.x === xyPointB.x && xyPointA.y == xyPointB.y;
}


export const describeLepton = (l: LeptonState) => {
  return `Lepton ${l.leptonId} at ${l.x},${l.y} moving ${l.direction.label}`;
};

export const describeLeptons = (ls: LeptonState[]) => {
  return `${ls.length} leptons; ${ls.map(describeLepton).join(". ")}`;
};

